import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Gate } from '../../../global/contexts/auth/auth';
import { SEARCH_DEBOUNCE_MS } from '../../../core/constants/forms';
import { IconCalculator, IconChart, IconEdit, IconPlus, IconReceipt } from '../../../common/icons';
import { useClientsList } from '../contexts/clients';
import SectionTabs from '../../navigation/section-tabs';
import { CLIENT_TABS } from '../../navigation/tabs';

import '../styles/overview.css';

const STATUS_LABEL = { 2: 'Activo', 1: 'Inactivo', 0: 'Eliminado' };

export default function ClientsOverview() {
    const [params, setParams] = useSearchParams();
    const urlSearch = params.get('q') || '';

    const [search, setSearch] = useState(urlSearch);
    const [debounced, setDebounced] = useState(urlSearch);

    useEffect(() => {
        const t = setTimeout(() => setDebounced(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => {
        const next = new URLSearchParams(params);
        if (debounced) next.set('q', debounced); else next.delete('q');
        setParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debounced]);

    const filters = useMemo(() => (debounced ? { q: debounced } : {}), [debounced]);
    const { data, isLoading, isFetching, error } = useClientsList(filters);
    const clients = data?.items ?? [];
    const total = data?.pagination?.total ?? clients.length;

    return (
        <div className="client-overview">
            <SectionTabs tabs={CLIENT_TABS} />
            <header className="client-overview__header">
                <div className="client-overview__heading">
                    <h1 className="client-overview__title">Clientes</h1>
                    <span className="client-overview__count">{total}</span>
                </div>
                <Gate domain="clients" action="write">
                    <Link to="/admin/clients/new" className="client-overview__add">
                        <IconPlus />
                        <span>Nuevo cliente</span>
                    </Link>
                </Gate>
            </header>

            <div className="client-overview__toolbar">
                <div className="client-overview__search">
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar por nombre, documento, correo o teléfono…"
                        aria-label="Buscar clientes por nombre, documento, correo o teléfono"
                    />
                </div>
            </div>

            {error && (
                <div className="client-overview__error">
                    {error.message || 'No se pudieron cargar los clientes'}
                </div>
            )}

            {isLoading ? (
                <div className="client-overview__state">Cargando clientes…</div>
            ) : !clients.length ? (
                <div className="client-overview__state">
                    <p className="client-overview__state-text">
                        {debounced ? 'Ningún cliente coincide con la búsqueda.' : 'Aún no hay clientes.'}
                    </p>
                    {!debounced && (
                        <Gate domain="clients" action="write">
                            <Link to="/admin/clients/new" className="client-overview__add">
                                <IconPlus />
                                <span>Crear el primer cliente</span>
                            </Link>
                        </Gate>
                    )}
                </div>
            ) : (
                <div className={`client-overview__card ${isFetching ? 'client-overview__card--busy' : ''}`}>
                    <table className="client-table">
                        <thead>
                            <tr>
                                <th>Nombre</th>
                                <th>Documento</th>
                                <th>Teléfono</th>
                                <th>Estado</th>
                                <th className="client-table__actions-col" aria-label="Acciones" />
                            </tr>
                        </thead>
                        <tbody>
                            {clients.map((c) => (
                                <tr key={c.id} className="client-table__row">
                                    <td>
                                        <Link to={`/admin/clients/${c.id}`} className="client-table__name" title="Ver cliente">{c.name}</Link>
                                    </td>
                                    <td>{c.document || <span className="client-table__empty">—</span>}</td>
                                    <td>{c.phone || <span className="client-table__empty">—</span>}</td>
                                    <td>
                                        <span className={`client-badge client-badge--${c.status}`}>{STATUS_LABEL[c.status] || '—'}</span>
                                    </td>
                                    <td className="client-table__actions">
                                        <Gate domain="kpis" action="read">
                                            <Link
                                                to={`/admin/clients/${c.id}/stats`}
                                                className="client-table__edit"
                                                aria-label={`Estadísticas de ${c.name}`}
                                                title="Estadísticas"
                                            >
                                                <IconChart />
                                            </Link>
                                        </Gate>
                                        <Gate domain="billing" action="read">
                                            <Link
                                                to={`/admin/clients/${c.id}/liquidation`}
                                                className="client-table__edit"
                                                aria-label={`Liquidar ${c.name}`}
                                                title="Liquidar"
                                            >
                                                <IconReceipt />
                                            </Link>
                                            <Link
                                                to={`/admin/clients/${c.id}/account`}
                                                className="client-table__edit"
                                                aria-label={`Estado de cuenta de ${c.name}`}
                                                title="Estado de cuenta"
                                            >
                                                <IconCalculator />
                                            </Link>
                                        </Gate>
                                        <Gate domain="clients" action="write">
                                            <Link
                                                to={`/admin/clients/${c.id}/edit`}
                                                className="client-table__edit"
                                                aria-label={`Editar ${c.name}`}
                                                title="Editar"
                                            >
                                                <IconEdit />
                                            </Link>
                                        </Gate>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
